/*
 * A status badge: a tone and, always, the words.
 *
 * `children` is required and typed as required, because a badge that carries
 * its meaning in its colour alone is not a badge, it is a coloured rectangle.
 * The failure is not hypothetical in this product: the console's densest screen
 * is a table of broadcasts whose whole point is the difference between sent and
 * played, and a reader who cannot separate Forest from Sahel would be looking
 * at two identical pills.
 *
 * The optional dot is a second, redundant channel for scanning a column
 * quickly. It is `aria-hidden`, because the word beside it already says the
 * same thing and hearing it twice is noise.
 */

import type { HTMLAttributes, ReactNode } from 'react'
import type { Tone } from '../tone'
import styles from './Badge.module.css'

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  tone?: Tone
  /** Required. The status, in words. */
  children: ReactNode
  /** A leading mark, for columns that are scanned rather than read. */
  dot?: boolean
}

export function Badge({ tone = 'neutral', dot = false, children, className, ...rest }: BadgeProps) {
  const badgeClassName = [styles.badge, styles[tone], className].filter(Boolean).join(' ')

  return (
    <span {...rest} className={badgeClassName}>
      {dot ? <span className={styles.dot} aria-hidden="true" /> : null}
      {children}
    </span>
  )
}
